import { forwardRef } from "react"
import Select from "react-select"

import { ReactSelectProps } from "./ReactSelectProps"

const ReactSelect = (
  {
    name,
    value,
    options,
    onChange,
    disabled,
    isMulti,
    error,
    styles,
    isClearable = false,
    isSearchable = false,
    ...restProps
  }: ReactSelectProps,
  ref: any,
) => {
  const makeOptionsWithNoneExistedOption = () => {
    if (value === null || value === undefined || value === "") {
      return options
    }
    const values = Array.isArray(value) ? value : [value]

    const existedValues = new Set(options?.map((o) => o?.value))

    const missingOptions = values.filter((v) => !existedValues.has(v)).map((v) => ({ label: v, value: v }))

    if (missingOptions.length === 0) {
      return options
    }

    return [...(options || []), ...missingOptions]
  }

  const finalOption = makeOptionsWithNoneExistedOption()

  let transformedValue = null
  if (Array.isArray(value)) {
    transformedValue = finalOption?.filter((opt) => value?.includes(opt.value))
  } else {
    transformedValue = finalOption?.find((opt) => opt.value === value) || null
  }

  const selectStyles = {
    container: (state: any) => {
      return { ...state, pointerEvents: "unset" }
    },
    control: (state: any, { menuIsOpen }: any) => {
      let borderColor = "#e5e5e5"
      if (error) {
        borderColor = "#ff1923"
      } else if (menuIsOpen) {
        borderColor = "#323237"
      }

      const border = `solid 1px ${borderColor}`

      return {
        ...state,
        backgroundColor: `${disabled ? "rgb(241, 243, 243);" : "white"}`,
        border,
        borderRadius: "2px",
        height: "36px",
        minHeight: "36px",
        boxShadow: "0 0 0 1px transparent",
        ":hover": {
          border,
          boxShadow: "0 0 0 1px transparent",
        },
        ":focus": {
          border,
          boxShadow: "0 0 0 1px transparent",
        },
        cursor: disabled ? "not-allowed" : "auto",
      }
    },
    placeholder: (state: any) => {
      return {
        ...state,
        color: "#c2c5c8",
      }
    },
    valueContainer: (state: any) => ({
      ...state,
      height: "36px",
      padding: "0 6px",
    }),
    singleValue: (state: any) => {
      return {
        ...state,
        color: disabled ? "rgb(185, 188, 191)" : "inherit",
      }
    },
    input: (state: any) => ({
      ...state,
      margin: "0px",
    }),
    indicatorSeparator: (state: any) => ({
      display: "none",
    }),
    option: (state: any, { isDisabled, ...restProps }: any) => {
      const optStyles = {
        ...state,
        backgroundColor: "white",
        color: "#323237",
        ":hover": {
          backgroundColor: "rgba(0, 215, 255, 0.15)",
        },
      }

      if (isDisabled) {
        optStyles.cursor = "not-allowed"
        optStyles.color = "#b9bcbf"
        optStyles[":hover"] = {}
      }

      return optStyles
    },
    menuPortal: (state: any) => ({ ...state, zIndex: 9999 }),
    indicatorsContainer: (state: any) => ({
      ...state,
      height: "36px",
    }),
  }

  const handleOnChange = (newValue: any, e: any) => {
    let transformedValue = null
    if (Array.isArray(newValue)) {
      transformedValue = newValue?.map((opt: any) => opt.value)
    } else {
      transformedValue = newValue?.value
    }

    onChange && onChange(transformedValue, e)
  }

  return (
    <div data-name={name}>
      <Select
        ref={ref}
        name={name}
        styles={{ ...selectStyles, ...styles }}
        classNamePrefix="select"
        isDisabled={disabled}
        isClearable={isClearable}
        isSearchable={isSearchable}
        options={finalOption}
        value={transformedValue}
        onChange={handleOnChange}
        isMulti={isMulti}
        menuPortalTarget={document.body}
        {...restProps}
      />
    </div>
  )
}

export default forwardRef(ReactSelect)
